let analyticsLoadPromise: Promise<boolean> | null = null;
let analyticsConfigured = false;

declare global {
  interface Window {
    dataLayer?: unknown[][];
    gtag?: (...args: unknown[]) => void;
    __kfGaLoaded?: boolean;
  }
}

function isBrowser() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

export function getGoogleAnalyticsMeasurementId() {
  return import.meta.env.VITE_GA_MEASUREMENT_ID?.trim() || '';
}

function ensureDataLayer() {
  if (!isBrowser()) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag(...args: unknown[]) {
      window.dataLayer?.push(args);
    };
}

function applyGoogleConsent(analyticsGranted: boolean) {
  if (!window.gtag) {
    return;
  }

  window.gtag('consent', 'update', {
    analytics_storage: analyticsGranted ? 'granted' : 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
}

export async function enableGoogleAnalytics() {
  const measurementId = getGoogleAnalyticsMeasurementId();

  if (!isBrowser() || !measurementId) {
    return false;
  }

  if (window.__kfGaLoaded) {
    applyGoogleConsent(true);
    return true;
  }

  if (analyticsLoadPromise) {
    return analyticsLoadPromise;
  }

  analyticsLoadPromise = new Promise<boolean>((resolve) => {
    ensureDataLayer();

    const existingScript = document.getElementById('kf-ga4-script') as HTMLScriptElement | null;

    const finishSetup = () => {
      ensureDataLayer();

      if (!analyticsConfigured && window.gtag) {
        window.gtag('consent', 'default', {
          analytics_storage: 'denied',
          ad_storage: 'denied',
          ad_user_data: 'denied',
          ad_personalization: 'denied',
        });
        window.gtag('js', new Date());
        window.gtag('config', measurementId, {
          send_page_view: false,
          anonymize_ip: true,
          allow_google_signals: false,
          allow_ad_personalization_signals: false,
          transport_type: 'beacon',
        });
        analyticsConfigured = true;
      }

      applyGoogleConsent(true);
      window.__kfGaLoaded = true;
      resolve(true);
    };

    if (existingScript) {
      if (existingScript.dataset.loaded === 'true') {
        finishSetup();
        return;
      }

      existingScript.addEventListener('load', finishSetup, { once: true });
      existingScript.addEventListener(
        'error',
        () => {
          analyticsLoadPromise = null;
          resolve(false);
        },
        { once: true },
      );
      return;
    }

    const script = document.createElement('script');
    script.id = 'kf-ga4-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    script.onload = () => {
      script.dataset.loaded = 'true';
      finishSetup();
    };
    script.onerror = () => {
      analyticsLoadPromise = null;
      resolve(false);
    };

    document.head.appendChild(script);
  });

  return analyticsLoadPromise;
}

export function disableGoogleAnalytics() {
  if (!isBrowser() || !window.gtag) {
    return;
  }

  applyGoogleConsent(false);
}

export function trackGoogleAnalyticsPageView(path: string, title: string) {
  const measurementId = getGoogleAnalyticsMeasurementId();

  if (!isBrowser() || !measurementId || !window.gtag || !window.__kfGaLoaded) {
    return;
  }

  const url = new URL(window.location.href);
  url.pathname = path.split('?')[0] || '/';
  url.search = path.includes('?') ? `?${path.split('?')[1]}` : window.location.search;

  window.gtag('event', 'page_view', {
    page_title: title,
    page_path: path,
    page_location: url.toString(),
    send_to: measurementId,
  });
}
